<template>
  <div>
    <a-card title="税筹商户统计流水" style="margin: 20px">
      <!-- ============ 顶部筛选区 ============ -->
      <div class="filter-bar">
        <a-tree-select
            v-model="filterForm.departmentCode"
            :dropdown-style="{ maxHeight: '400px', overflow: 'auto' }"
            :tree-data="departmentTree"
            allow-clear
            placeholder="所属机构"
            style="width: 200px;"
            tree-default-expand-all
            @change="onSearch"
        />
        <!-- 自然周期快捷按钮 -->
        <a-button-group size="small" style="margin-right: 8px;">
          <a-button @click="onQuickDate('today')">今天</a-button>
          <a-button @click="onQuickDate('month')">本月</a-button>
          <a-button @click="onQuickDate('quarter')">本季度</a-button>
          <a-button @click="onQuickDate('year')">今年</a-button>
        </a-button-group>
        <a-range-picker
            v-model="timeRange"
            :disabled-date="disabledDate"
            style="width: 280px;"
            value-format="YYYY-MM-DD"
            @change="onSearch"
        />
        <a-button type="primary" @click="onSearch">查询</a-button>
        <a-button @click="onReset">重置</a-button>
        <a-button
            v-if="$authority('merchantStatExport')"
            icon="download"
            type="link"
            @click="onExportSummary"
        >导出汇总
        </a-button>

      </div>

      <!-- ============ 视图模式切换 ============ -->
      <div class="view-mode-bar">
        <a-radio-group v-model="viewMode" @change="onViewModeChange">
          <a-radio-button value="cockpit">驾驶舱</a-radio-button>
          <a-radio-button value="list">列表</a-radio-button>
        </a-radio-group>
      </div>

      <!-- ============ 全局汇总 4 卡 ============ -->
      <GlobalSummaryCards :summary="cockpitData?.summary" :loading="cockpitLoading"/>

      <!-- ============ 驾驶舱图表 + 联动表 ============ -->
      <GlobalCockpitChart
          v-if="viewMode === 'cockpit'"
          :data="cockpitData"
          :loading="cockpitLoading"
      />

      <!-- ============ 商户卡片网格 ============ -->
      <MerchantCardGrid
          v-if="viewMode === 'cockpit'"
          :cards="merchantCards"
          :loading="cardsLoading"
          :pagination="cardsPagination"
          @view-flow="openMerchantFlow"
          @page-change="onCardsPageChange"
      />

      <!-- ============ 列表视图（分页表格） ============ -->
      <a-table
          v-if="viewMode === 'list'"
          :columns="listColumns"
          :data-source="merchantCards"
          :loading="cardsLoading"
          :pagination="cardsPagination"
          :row-key="(record) => record.merchantNo"
          bordered
          size="small"
          @change="onTableChange"
      >
        <template slot="action" slot-scope="text, record">
          <a-button type="link" @click="openMerchantFlow(record.merchantNo)">查看流水</a-button>
        </template>
      </a-table>

      <!-- ============ 商户流水弹窗 ============ -->
      <MerchantFlowModal
          :visible="flowModalVisible"
          :merchant-no="currentMerchantNo"
          :time-range="timeRange"
          @close="flowModalVisible = false"
      />
    </a-card>
  </div>
</template>

<script>
import dayjs from 'dayjs';
import quarterOfYear from 'dayjs/plugin/quarterOfYear';
dayjs.extend(quarterOfYear);
import {message} from 'ant-design-vue';
import GlobalSummaryCards from './components/GlobalSummaryCards.vue';
import GlobalCockpitChart from './components/GlobalCockpitChart.vue';
import MerchantCardGrid from './components/MerchantCardGrid.vue';
import MerchantFlowModal from './components/MerchantFlowModal.vue';

export default {
  name: 'TaxMerchantStat',
  components: {
    GlobalSummaryCards,
    GlobalCockpitChart,
    MerchantCardGrid,
    MerchantFlowModal,
  },
  data() {
    return {
      // ========== 筛选 ==========
      filterForm: {
        departmentCode: undefined,
        status: undefined,  // 不传 status，让后端走默认 ne(status, 3) 排除失败，过滤 0/1/2 所有成功订单
        keyword: '',
      },
      timeRange: [
        // 默认本季度（自然季度边界）
        dayjs().startOf('quarter').format('YYYY-MM-DD'),
        dayjs().endOf('quarter').format('YYYY-MM-DD'),
      ],
      departmentTree: [], // 部门树（暂留接口位）

      // ========== 视图模式 ==========
      viewMode: 'cockpit',

      // ========== 数据 ==========
      cockpitData: null,
      cockpitLoading: false,
      merchantCards: [],
      cardsLoading: false,
      cardsPagination: {
        current: 1,
        pageSize: 12,
        total: 0,
      },

      // ========== 列表视图列 ==========
      listColumns: [
        {title: '商户编号', dataIndex: 'merchantNo', width: 130, align: 'center'},
        {title: '商户名称', dataIndex: 'merchantName', width: 150, align: 'center'},
        // {
        //   title: '清分协议', dataIndex: 'clearAgreementStatus', width: 100, align: 'center',
        //   customRender: (text) => text === '已配置'
        //       ? <a-tag color="green">已配置</a-tag>
        //       : <a-tag>未配置</a-tag>,
        // },
        {title: '订单总额', dataIndex: 'totalAmount', width: 140, align: 'center'},
        {title: '订单笔数', dataIndex: 'totalCount', width: 100, align: 'center'},
        {title: '服务费', dataIndex: 'serviceFee', width: 140, align: 'right'},
        {title: '分润', dataIndex: 'profitShare', width: 140, align: 'right'},
        {
          title: '成功率', dataIndex: 'successRate', width: 100, align: 'center',
          customRender: (text) => `${text || 0}%`,
        },
        {
          title: '操作', key: 'action', width: 160, fixed: 'right', align: 'center',
          scopedSlots: {customRender: 'action'},
        },
      ],

      // ========== 弹窗 ==========
      flowModalVisible: false,
      currentMerchantNo: '',
    };
  },
  mounted() {
    this.loadCockpit();
    this.loadCards();
  },
  methods: {
    /**
     * 加载驾驶舱数据（汇总 + TOP10 + 联动）
     */
    async loadCockpit() {
      this.cockpitLoading = true;
      try {
        const params = {
          departmentCode: this.filterForm.departmentCode || undefined,
          startTime: this.timeRange[0],
          endTime: this.timeRange[1],
          status: this.filterForm.status,
        };
        const res = await this.api.getMerchantStatCockpit(params);
        if (res.code === 200) {
          this.cockpitData = res.data;
        } else {
          message.error(res.info || '获取驾驶舱数据失败');
          this.cockpitData = null;
        }
      } catch (e) {
        console.error('加载驾驶舱数据异常', e);
        message.error('加载驾驶舱数据失败');
        this.cockpitData = null;
      } finally {
        this.cockpitLoading = false;
      }
    },

    /**
     * 加载商户卡片网格
     */
    async loadCards() {
      this.cardsLoading = true;
      try {
        const req = {
          departmentCode: this.filterForm.departmentCode || undefined,
          startTime: this.timeRange[0],
          endTime: this.timeRange[1],
          status: this.filterForm.status,
          keyword: this.filterForm.keyword || undefined,
          pageNo: this.cardsPagination.current,
          pageSize: this.cardsPagination.pageSize,
        };
        const res = await this.api.getMerchantStatCards(req);
        if (res.code === 200) {
          this.merchantCards = res.data.records || [];
          this.cardsPagination.total = res.data.total || 0;
        } else {
          message.error(res.info || '获取商户卡片数据失败');
          this.merchantCards = [];
          this.cardsPagination.total = 0;
        }
      } catch (e) {
        console.error('加载商户卡片异常', e);
        message.error('加载商户卡片失败');
        this.merchantCards = [];
      } finally {
        this.cardsLoading = false;
      }
    },

    /**
     * 查询按钮
     */
    onSearch() {
      this.cardsPagination.current = 1;
      this.loadCockpit();
      this.loadCards();
    },

    /**
     * 重置筛选
     */
    onReset() {
      this.filterForm.departmentCode = undefined;
      this.filterForm.status = undefined;  // 不限状态（仅排除失败）
      this.filterForm.keyword = '';
      this.timeRange = [
        // 重置为当前自然季度
        dayjs().startOf('quarter').format('YYYY-MM-DD'),
        dayjs().endOf('quarter').format('YYYY-MM-DD'),
      ];
      this.onSearch();
    },

    /**
     * 视图切换
     */
    onViewModeChange() {
      // 切换视图不重新加载
    },

    /**
     * 卡片网格分页变化
     */
    onCardsPageChange(page) {
      this.cardsPagination.current = page;
      this.loadCards();
    },

    /**
     * 表格视图分页变化
     */
    onTableChange(pagination) {
      this.cardsPagination.current = pagination.current;
      this.cardsPagination.pageSize = pagination.pageSize;
      this.loadCards();
    },

    /**
     * 打开商户流水弹窗
     */
    openMerchantFlow(merchantNo) {
      this.currentMerchantNo = merchantNo;
      this.flowModalVisible = true;
    },

    /**
     * 导出汇总
     */
    async onExportSummary() {
      if (!this.cockpitData?.summary) {
        message.warning('暂无数据可导出');
        return;
      }
      try {
        const xlsx = await import('xlsx');
        const {utils, writeFile} = xlsx;

        const summary = this.cockpitData.summary;
        const wb = utils.book_new();

        // Sheet 1: 全局汇总
        const summaryRows = [
          ['统计区间', `${this.timeRange[0]} ~ ${this.timeRange[1]}`],
          [],
          ['指标', '数值', '环比'],
          ['订单总额（元）', summary.totalAmount || 0, `${summary.amountRatio || 0}%`],
          ['订单笔数', summary.totalCount || 0, '-'],
          ['服务费收入（元）', summary.totalServiceFee || 0, `${summary.serviceFeeRatio || 0}%`],
          ['服务费笔数', summary.serviceFeeCount || 0, '-'],
          ['分润支出（元）', summary.totalProfitShare || 0, `${summary.profitShareRatio || 0}%`],
          ['分润笔数', summary.profitShareCount || 0, '-'],
          ['总商户数', summary.merchantCount || 0, '-'],
          ['活跃商户数', summary.activeMerchantCount || 0, '-'],
        ];
        const ws1 = utils.aoa_to_sheet(summaryRows);
        ws1['!cols'] = [{wch: 20}, {wch: 18}, {wch: 10}];
        utils.book_append_sheet(wb, ws1, '全局汇总');

        // Sheet 2: TOP10 商户
        if (this.cockpitData.topMerchants?.length) {
          const topRows = [
            ['TOP10 商户'],
            [],
            ['商户编号', '商户名称', '订单总额', '订单笔数', '服务费', '分润'],
            ...this.cockpitData.topMerchants.map((m) => [
              m.merchantNo, m.merchantName,
              m.totalAmount || 0, m.totalCount || 0,
              m.serviceFee || 0, m.profitShare || 0,
            ]),
          ];
          const ws2 = utils.aoa_to_sheet(topRows);
          ws2['!cols'] = [{wch: 14}, {wch: 20}, {wch: 14}, {wch: 10}, {wch: 14}, {wch: 14}];
          utils.book_append_sheet(wb, ws2, 'TOP10');
        }

        // Sheet 3: 日期×商户联动表
        if (this.cockpitData.dailyByMerchant?.length) {
          const linkedRows = [
            ['日期×商户联动表'],
            [],
            ['日期', '商户编号', '商户名称', '金额', '笔数'],
            ...this.cockpitData.dailyByMerchant.map((d) => [
              d.date, d.merchantNo, d.merchantName,
              d.amount || 0, d.count || 0,
            ]),
          ];
          const ws3 = utils.aoa_to_sheet(linkedRows);
          ws3['!cols'] = [{wch: 14}, {wch: 16}, {wch: 20}, {wch: 14}, {wch: 10}];
          utils.book_append_sheet(wb, ws3, '联动表');
        }

        const filename = `税筹商户统计汇总-${this.timeRange[0]}-${this.timeRange[1]}.xlsx`;
        writeFile(wb, filename);
        message.success('导出成功');
      } catch (e) {
        console.error('导出异常', e);
        message.error('导出失败');
      }
    },

    /**
     * 禁用未来日期
     */
    disabledDate(current) {
      return current && current > dayjs().endOf('day');
    },

    /**
     * 自然周期快捷日期
     * @param type today/month/quarter/year
     */
    onQuickDate(type) {
      const now = dayjs();
      switch (type) {
        case 'today':
          this.timeRange = [now.format('YYYY-MM-DD'), now.format('YYYY-MM-DD')];
          break;
        case 'month':
          this.timeRange = [now.startOf('month').format('YYYY-MM-DD'), now.endOf('month').format('YYYY-MM-DD')];
          break;
        case 'quarter':
          this.timeRange = [now.startOf('quarter').format('YYYY-MM-DD'), now.endOf('quarter').format('YYYY-MM-DD')];
          break;
        case 'year':
          this.timeRange = [now.startOf('year').format('YYYY-MM-DD'), now.endOf('year').format('YYYY-MM-DD')];
          break;
      }
      this.onSearch();
    },
  },
};
</script>

<style lang="less" scoped>
.filter-bar {
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
  align-items: center;
  margin-bottom: 16px;
  padding-bottom: 16px;
  border-bottom: 1px solid #f0f0f0;
}

.view-mode-bar {
  margin-bottom: 16px;
}

/deep/ .ant-card {
  margin-bottom: 16px;
}
</style>